import type { DataProvider } from '@refinedev/core'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

// Get access token from localStorage
const getAccessToken = (): string | null => {
  return localStorage.getItem('admin_access_token')
}

// Helper to get auth headers
const getAuthHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  }

  const token = getAccessToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  return headers
}

// Map resource names to API endpoints
const getResourceUrl = (resource: string): string => {
  const resourceMap: Record<string, string> = {
    patients: 'admin/patients',
    doctors: 'admin/doctors',
    appointments: 'admin/appointments',
    'lab-results': 'admin/lab-results',
    orders: 'admin/orders',
    'quiz-submissions': 'admin/quiz-submissions',
    users: 'admin/users',
    consultations: 'admin/consultations',
    'social-media': 'admin/social-media/posts',
    'social-accounts': 'admin/social-media/accounts',
    products: 'admin/products',
    categories: 'admin/categories',
    blog: 'admin/blog-posts',
  }
  return resourceMap[resource] || resource
}

// Custom error class that includes status for Refine's onError handler
class HttpError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.status = status
    this.name = 'HttpError'
  }
}

// Force redirect to login and clear auth data
const handleAuthError = () => {
  console.log('[dataProvider] 401 detected - clearing auth and redirecting')
  localStorage.removeItem('admin_access_token')
  localStorage.removeItem('admin_user')
  localStorage.removeItem('admin_user_role')

  // Hard redirect to login
  if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
    window.location.href = '/login'
  }
}

// Unified response handler - throws HttpError with status for 401s
const handleResponse = async (response: Response, errorMessage: string) => {
  if (!response.ok) {
    if (response.status === 401) {
      // Immediately handle auth error with hard redirect
      handleAuthError()
      // Also throw error for Refine's onError handler
      throw new HttpError('Session expired. Please login again.', 401)
    }

    const errorData = await response.json().catch(() => ({}))
    throw new HttpError(
      errorData.message || errorMessage,
      response.status
    )
  }

  return response.json()
}

// Custom data provider for NestJS API
export const dataProvider: DataProvider = {
  getList: async ({ resource, pagination, filters, sorters }) => {
    // Handle both v4 (current/pageSize) and v5 (current/pageSize) pagination
    const paginationOptions = pagination as { current?: number; pageSize?: number } | undefined
    const { current = 1, pageSize = 10 } = paginationOptions ?? {}

    const query = new URLSearchParams()
    query.set('page', String(current))
    query.set('pageSize', String(pageSize))

    // Handle sorting
    if (sorters && sorters.length > 0) {
      const { field, order } = sorters[0]
      query.set('sortBy', field)
      query.set('sortOrder', order)
    }

    // Handle filters
    if (filters) {
      filters.forEach((filter) => {
        if ('field' in filter && filter.value !== undefined) {
          // Map common filter names
          if (filter.field === 'search' && filter.operator === 'contains') {
            query.set('search', String(filter.value))
          } else {
            query.set(filter.field, String(filter.value))
          }
        }
      })
    }

    const resourceUrl = getResourceUrl(resource)
    const headers = getAuthHeaders()

    const response = await fetch(`${API_URL}/${resourceUrl}?${query}`, {
      headers,
    })

    const data = await handleResponse(response, `Failed to fetch ${resource}`)

    // Handle both paginated and non-paginated responses
    if (Array.isArray(data)) {
      return {
        data,
        total: data.length,
      }
    }

    return {
      data: data.data || data.items || [],
      total: data.total || data.meta?.total || 0,
    }
  },

  getOne: async ({ resource, id }) => {
    const resourceUrl = getResourceUrl(resource)
    const headers = getAuthHeaders()

    const response = await fetch(`${API_URL}/${resourceUrl}/${id}`, {
      headers,
    })

    const data = await handleResponse(response, `Failed to fetch ${resource}/${id}`)

    return { data }
  },

  create: async ({ resource, variables }) => {
    const resourceUrl = getResourceUrl(resource)
    const headers = getAuthHeaders()

    const response = await fetch(`${API_URL}/${resourceUrl}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(variables),
    })

    const data = await handleResponse(response, `Failed to create ${resource}`)

    return { data }
  },

  update: async ({ resource, id, variables }) => {
    const resourceUrl = getResourceUrl(resource)
    const headers = getAuthHeaders()

    const response = await fetch(`${API_URL}/${resourceUrl}/${id}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(variables),
    })

    const data = await handleResponse(response, `Failed to update ${resource}/${id}`)

    return { data }
  },

  deleteOne: async ({ resource, id }) => {
    const resourceUrl = getResourceUrl(resource)
    const headers = getAuthHeaders()

    const response = await fetch(`${API_URL}/${resourceUrl}/${id}`, {
      method: 'DELETE',
      headers,
    })

    const data = await handleResponse(response, `Failed to delete ${resource}/${id}`)
      .catch(() => ({ id }))

    return { data }
  },

  getApiUrl: () => API_URL,

  // Optional methods
  getMany: async ({ resource, ids }) => {
    const resourceUrl = getResourceUrl(resource)
    const headers = getAuthHeaders()

    const promises = ids.map(async (id) => {
      const response = await fetch(`${API_URL}/${resourceUrl}/${id}`, { headers })
      return handleResponse(response, `Failed to fetch ${resource}/${id}`)
    })

    const data = await Promise.all(promises)

    return { data }
  },

  createMany: async ({ resource, variables }) => {
    const resourceUrl = getResourceUrl(resource)
    const headers = getAuthHeaders()

    const promises = variables.map(async (vars) => {
      const response = await fetch(`${API_URL}/${resourceUrl}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(vars),
      })
      return handleResponse(response, `Failed to create ${resource}`)
    })

    const data = await Promise.all(promises)

    return { data }
  },

  updateMany: async ({ resource, ids, variables }) => {
    const resourceUrl = getResourceUrl(resource)
    const headers = getAuthHeaders()

    const promises = ids.map(async (id) => {
      const response = await fetch(`${API_URL}/${resourceUrl}/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(variables),
      })
      return handleResponse(response, `Failed to update ${resource}/${id}`)
    })

    const data = await Promise.all(promises)

    return { data }
  },

  deleteMany: async ({ resource, ids }) => {
    const resourceUrl = getResourceUrl(resource)
    const headers = getAuthHeaders()

    const promises = ids.map(async (id) => {
      const response = await fetch(`${API_URL}/${resourceUrl}/${id}`, {
        method: 'DELETE',
        headers,
      })
      return handleResponse(response, `Failed to delete ${resource}/${id}`)
        .catch(() => ({ id }))
    })

    const data = await Promise.all(promises)

    return { data }
  },

  custom: async ({ url, method, payload, headers }) => {
    const authHeaders = getAuthHeaders()

    const response = await fetch(`${API_URL}${url}`, {
      method: method || 'GET',
      headers: {
        ...authHeaders,
        ...headers,
      },
      body: payload ? JSON.stringify(payload) : undefined,
    })

    const data = await handleResponse(response, `Request to ${url} failed`)

    return { data }
  },
}
